import React, { useEffect } from "react";

import { useMutation } from "@apollo/client";
import { Box, Button, FormLabel, Grid, TextField } from "@mui/material";
import * as yup from "yup";

import Dialog, { DialogProps } from "@/components/Dialog";
import { graphql } from "@/gql";
import useFormWithYup from "@/hooks/useFormWithYup";
import useGlobalDialog from "@/hooks/useGlobalDialog";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import {
  displayError,
  getBaseErrorMessages,
  getValidationErrors,
  parseInvalidError,
} from "@/utils/errors";
import { muiFieldError } from "@/utils/hookForm";

interface Props extends DialogProps {
  clientId: string;
  defaultEmail?: string;
  afterInvite?: () => void | Promise<void>;
}

const validationSchema = yup
  .object()
  .required()
  .shape({
    email: yup.string().label("Email").presence().required().email(),
  });

const inviteClientMutation = graphql(`
  mutation InviteClient2($input: InviteClientInput!) {
    inviteClient(input: $input) {
      clients {
        id
        email
        invitedAt
      }
    }
  }
`);

const InviteClientDialog: React.FC<Props> = ({
  clientId,
  defaultEmail = "",
  afterInvite = () => {},
  ...dialogProps
}) => {
  const snackbar = useGlobalSnackbar();
  const { alert } = useGlobalDialog();
  const [inviteClient] = useMutation(inviteClientMutation);

  const {
    register,
    reset,
    setError,
    handleSubmit,
    formState: { errors },
  } = useFormWithYup({
    defaultValues: { email: defaultEmail },
    validationSchema,
  });

  useEffect(() => {
    reset({ email: defaultEmail });
  }, [defaultEmail]);

  async function onSubmit(values: ReturnType<typeof validationSchema.cast>) {
    try {
      await inviteClient({
        variables: {
          input: {
            attributes: [{ id: clientId, email: values.email }],
          },
        },
      });
      snackbar.open("Client was sent an invite to use mobile app.");
      return afterInvite();
    } catch (error) {
      const invalidError = parseInvalidError(error);
      if (invalidError) {
        const validationErrors = getValidationErrors(invalidError);
        const baseErrs = getBaseErrorMessages(validationErrors);
        if (baseErrs) {
          alert({
            title: "Unable to invite client",
            message: baseErrs,
          });
        }
        validationErrors.forEach((error) => {
          setError("email", { message: error.message });
        });
      }
      alert({
        title: "Unable to invite client",
        message: displayError(error),
      });
    }
  }

  return (
    <Dialog
      {...dialogProps}
      title="Invite your client to use the mobile app"
      content={
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormLabel>Email</FormLabel>
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register("email")}
                {...muiFieldError(errors.email)}
                type="email"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} />
            <Grid item xs={12}>
              <Box display="flex" justifyContent="center">
                <Button type="submit" size="large">
                  Send
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      }
    />
  );
};

export default InviteClientDialog;
